<template>
  <div class="entity-selector">
    <v-select
      v-model="selectedEntity"
      :items="entities"
      :loading="isLoading"
      :item-text="itemText"
      :item-value="itemValue"
      :hint="hint"
      :rules="rules"
      :disabled="disabled"
      :outlined="outlined"
      :dense="dense"
      :hide-details="hideDetails"
      :clearable="clearable"
      @input="onInput"
    >
      <template #append-outer>
        <v-btn
          v-if="!selectionOnly"
          icon
          small
          color="primary"
          @click.stop="openAddDialog"
          :disabled="disabled"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>
              {{ noDataText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'

export default {
  name: 'EntitySelector',
  components: {},
  props: {
    value: {
      type: [Object, String, Number, null],
      default: null
    },
    loadFunction: {
      type: Function,
      required: true
    },
    addFunction: {
      type: Function,
      required: false,
      default: () => {
      }
    },
    formSchemas: {
      type: Array,
      default: () => []
    },
    itemText: {
      type: String,
      default: 'name'
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    hint: {
      type: String,
      default: ''
    },
    dialogTitle: {
      type: String,
      default: 'Add New Entity'
    },
    noDataText: {
      type: String,
      default: 'No data available'
    },
    saveBtnName: {
      type: String,
      default: 'save'
    },
    rules: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: true
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    selectionOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      entities: [],
      selectedEntity: null,
      isLoading: false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (newValue !== this.selectedEntity) {
          this.selectedEntity = newValue
        }
      }
    }
  },
  mounted () {
    this.loadEntities()
  },
  methods: {
    async loadEntities () {
      try {
        this.isLoading = true
        this.entities = await this.loadFunction()
        console.log('entities', this.entities)

        // If value is set but selectedEntity is not, set the selectedEntity to the value
        if (this.value && !this.selectedEntity) {
          this.selectedEntity = this.value
        }
      } catch (error) {
        console.error('Error loading entities:', error)
        this.$emit('error', error)
      } finally {
        this.isLoading = false
      }
    },
    onInput (value) {
      console.log('onInput', value)
      this.$emit('input', value)
    },
    async openAddDialog () {
      const store = useBaseFormDialogStore()

      // Define the schema for the form
      const schema = {
        title: this.dialogTitle,
        subtitle: '',
        schemas: this.formSchemas
      }

      // Open the dialog and wait for the result
      const formData = await store.editItem(schema)

      // Handle the result
      if (formData) {
        await this.handleAddEntity(formData)
      }
    },
    async handleAddEntity (entityData) {
      try {
        this.isLoading = true
        const newEntity = await this.addFunction(entityData)

        // Reload entities to include the new one
        await this.loadEntities()

        // Select the newly created entity
        this.selectedEntity = newEntity[this.itemValue]
        this.$emit('input', newEntity[this.itemValue])

        // Emit success event
        this.$emit('entity-added', newEntity)
      } catch (error) {
        console.error('Error adding entity:', error)
        this.$emit('error', error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.entity-selector {
  width: 100%;
}
</style>
